import { useMemo } from "react";
import AuthClient from "../api/AuthClient";
import PromoterGroupsApi from "../api/PromoterGroupsApi";
import { PartyModel } from "../types";
import { usePromoterGroup } from "./useServerDataQueries";

export type SelectedGroupRequestState = "idle" | "loading" | "success" | "error";

const inFlightGroupRequests = new Map<string, Promise<PartyModel | null>>();

export const fetchPromoterSelectedGroup = async (groupId: string): Promise<PartyModel | null> => {
  const existingRequest = inFlightGroupRequests.get(groupId);

  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    const api = new PromoterGroupsApi(new AuthClient());
    const response = await api.getPromoterGroup(groupId);

    if (!response?.data) {
      return null;
    }

    return response.data;
  })();

  inFlightGroupRequests.set(groupId, request);

  try {
    return await request;
  } finally {
    inFlightGroupRequests.delete(groupId);
  }
};

export const usePromoterSelectedGroup = (groupId?: string) => {
  const { data: selectedGroup = null, isError, isFetching, isLoading, isSuccess } =
    usePromoterGroup(groupId);

  const requestState: SelectedGroupRequestState = !groupId || isError
    ? "error"
    : isLoading || isFetching
      ? "loading"
      : isSuccess
        ? "success"
        : "idle";

  return useMemo(() => ({
    selectedGroup,
    requestState,
  }), [requestState, selectedGroup]);
};
